import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { Image, Col, Row, Form, Button, Container, InputGroup, Modal } from '@themesberg/react-bootstrap';
import { useHistory } from "react-router-dom";
import BgImage from "../assets/img/ME_background.jpg";
import brandME from "../assets/img/brand/me.webp";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { useEffect } from "react";
import { passwordRules } from "../config/passwordPolicy"; // adjust path as needed

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChecks, setPasswordChecks] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    hasMinLength: false,
    noSpaces: true,
    passwordsMatch: true,
  });

  useEffect(() => {
    const noLeadingOrTrailingSpaces = newPassword === newPassword.trim();

    setPasswordChecks({
      hasLower: passwordRules.lowerCaseRegex.test(newPassword),
      hasUpper: passwordRules.upperCaseRegex.test(newPassword),
      hasNumber: passwordRules.numberRegex.test(newPassword),
      hasSpecial: passwordRules.specialCharsRegex.test(newPassword),
      hasMinLength: newPassword.length >= passwordRules.minLength,
      noSpaces: noLeadingOrTrailingSpaces,
      passwordsMatch: newPassword === confirmPassword,
    });
  }, [newPassword, confirmPassword]);
  const [stage, setStage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const history = useHistory();
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    try {
      await resetPassword({ username: identifier });
      setModalMessage("A verification code has been sent to your email.");
      setShowModal(true); // Show the modal with the success message
      setStage(2);
    } catch (error) {
      console.error("Error requesting password reset code:", error);
      setModalMessage("Failed to request password reset code. Please check your username or email.");
      setShowModal(true);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      if (!isPasswordValid) {
        setModalMessage("Password does not meet the required criteria.");
        setShowModal(true);
        return;
      }
      await confirmResetPassword({ username: identifier, confirmationCode: code, newPassword });
      setModalMessage("Password has been successfully reset.");
      setShowModal(true);
      setTimeout(() => history.push("/login"), 2000); // Redirect to login page after 2 seconds
    } catch (error) {
      console.error("Error resetting password:", error);
      const fallbackMessage = "Failed to reset password. Please check the code and try again.";

      // Show detailed Cognito error if available
      if (error.name === "InvalidPasswordException") {
        setModalMessage(`Password does not meet policy: ${error.message}`);
      } else if (error.name === "CodeMismatchException") {
        setModalMessage("The verification code is incorrect or expired.");
      } else {
        setModalMessage(fallbackMessage);
      }

      setShowModal(true);
    }
  };

  return (
    <main style={{ backgroundImage: `url(${BgImage})`, minHeight: `100vh` }}>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <Row className="justify-content-center form-bg-image mb-2">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <Image src={brandME} alt="ME logo" className="image-sm"/>
            </Col>
          </Row>
          <Row className="justify-content-center form-bg-image">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <p className="text-left fs-3 fw-bold">Forgot Password</p>
                </div>

                {stage === 1 ? (
                  <Form className="mt-4" onSubmit={handleRequestCode}>
                    <Form.Group className="mb-4">
                      <Form.Label>Your Username or Email Address</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          id="identifier"
                          autoFocus
                          required
                          type="text"
                          placeholder="Enter your username or email"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100">
                      Request Code
                    </Button>
                  </Form>
                ) : (
                  <Form className="mt-4" onSubmit={handleResetPassword}>
                    <Form.Group className="mb-4">
                      <Form.Label>Verification Code</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id="code"
                          autoFocus
                          required
                          type="text"
                          placeholder="Enter the verification code"
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label>New Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id="newPassword"
                          required
                          type="password"
                          placeholder="Enter your new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label>Confirm Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id="confirmPassword"
                          required
                          type="password"
                          placeholder="Re-enter your new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>

                    {/* Password validation checklist */}
                    <ul style={{ fontSize: "0.9rem", paddingLeft: "1rem" }}>
                      <li style={{ color: passwordChecks.hasLower ? "green" : "red" }}>
                        {passwordChecks.hasLower ? "✓" : "✗"} Password must contain a lower case letter
                      </li>
                      <li style={{ color: passwordChecks.hasUpper ? "green" : "red" }}>
                        {passwordChecks.hasUpper ? "✓" : "✗"} Password must contain an upper case letter
                      </li>
                      <li style={{ color: passwordChecks.hasNumber ? "green" : "red" }}>
                        {passwordChecks.hasNumber ? "✓" : "✗"} Password must contain a number
                      </li>
                      <li style={{ color: passwordChecks.hasMinLength ? "green" : "red" }}>
                        {passwordChecks.hasMinLength ? "✓" : "✗"} Password must be at least 16 characters
                      </li>
                      <li style={{ color: passwordChecks.hasSpecial ? "green" : "red" }}>
                        {passwordChecks.hasSpecial ? "✓" : "✗"} Password must contain a special character or a space
                      </li>
                      <li style={{ color: passwordChecks.noSpaces ? "green" : "red" }}>
                        {passwordChecks.noSpaces ? "✓" : "✗"} No leading or trailing spaces
                      </li>
                      <li style={{ color: passwordChecks.passwordsMatch ? "green" : "red" }}>
                        {passwordChecks.passwordsMatch ? "✓" : "✗"} Passwords must match
                      </li>
                    </ul>
                    <Button variant="primary" type="submit" className="w-100" disabled={!isPasswordValid}>
                      Reset Password
                    </Button>
                  </Form>
                )}
              </div>
            </Col>
          </Row>
        </Container>

        {/* Modal for displaying messages */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Notification</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modalMessage}</Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      </section>
    </main>
  );
}
